import React from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { useOnClickOutside } from 'lib/utils';
import { LandingPageDesignAccess } from 'lib/api/superadmin/landingPageDesigns/useQueryLandingPageDesigns';
import { IoMdCheckmark } from 'react-icons/io';
import { Flex } from 'lib/components/styles/layout';
import { Button } from 'react-covideo-common';

const BadgeContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  max-width: 280px;
  height: 72px;
  white-space: nowrap;
  align-items: center;
  flex-wrap: wrap;
  position: relative;
`;

const PopupContainer = styled.div<{ show: boolean }>`
  position: absolute;
  z-index: 999;
  box-sizing: border-box;
  right: 72px;
  top: 80px;
  width: 315px;
  max-height: 240px;
  background-color: white;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  padding: 20px;
  border-radius: 8px;
  overflow-y: auto;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  border-radius: 8px;
  border: 1px solid rgba(230, 233, 238, 1);
`;

type StyleObject = {
  container?: React.CSSProperties;
  text?: React.CSSProperties;
};

interface IBadgesList {
  list: LandingPageDesignAccess[];
  extendStyles?: StyleObject;
}
const BadgesList = ({ list, extendStyles }: IBadgesList) => {
  const theme = useTheme();
  return (
    <>
      {list
        .map(access => [
          access.customer && (
            <Button
              key={`${access.id}-customer-${access.customer.customerId}`}
              text={
                access.customer.business || `ID: ${access.customer.customerId}`
              }
              extendStyles={extendStyles}
              variant='secondary'
              size='small'
              icon={
                access.customer.defaultLandingPageDesignId ===
                access.landingPageDesignId ? (
                  <IoMdCheckmark size={20} fill={theme.colors.primary[40]} />
                ) : undefined
              }
            />
          ),

          access.reseller && (
            <Button
              key={`${access.id}-reseller-${access.reseller.resellerId}`}
              text={access.reseller.resellerName}
              extendStyles={extendStyles}
              variant='secondary'
              size='small'
            />
          ),
        ])
        .flat()
        .filter(Boolean)}
    </>
  );
};

interface ILandingPageDesignBadgeList {
  accessList: LandingPageDesignAccess[];
  templateId: number;
  setMorePopup: React.Dispatch<React.SetStateAction<string | null>>;
  morePopup: string | null;
  togglePopup: (id: string | null) => void;
}
export const LandingPageDesignBadgeList = ({
  accessList,
  templateId,
  morePopup,
  togglePopup,
}: ILandingPageDesignBadgeList) => {
  const { ref } = useOnClickOutside(() => {
    togglePopup(null);
  });

  const maxBadges = 3;

  const sortedList = accessList.sort((a, b) => {
    const aMatch =
      a.landingPageDesignId ===
      (a.customer ? a.customer.defaultLandingPageDesignId : null);
    const bMatch =
      b.landingPageDesignId ===
      (b.customer ? b.customer.defaultLandingPageDesignId : null);
    if (aMatch === bMatch) return 0;
    return aMatch ? -1 : 1;
  });

  const showPopup = morePopup === `${templateId}-more-badge`;

  return (
    <BadgeContainer>
      <Flex flexDirection='row' flexWrap='wrap' gap='8px'>
        <BadgesList
          list={sortedList.slice(0, maxBadges)}
          extendStyles={{
            text: {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          }}
        />
        {sortedList.length > maxBadges && (
          <div onClick={() => togglePopup(`${templateId}-more-badge`)}>
            <Button
              key={`${templateId}-more-badge`}
              variant='secondary'
              size='small'
              text={'...More'}
              extendStyles={{
                container: {
                  cursor: 'pointer',
                },
              }}
            />
          </div>
        )}
      </Flex>

      {showPopup && (
        <PopupContainer key={templateId} show={showPopup} ref={ref}>
          <BadgesList
            list={accessList}
            extendStyles={{
              container: {
                maxWidth: 'auto',
              },
            }}
          />
        </PopupContainer>
      )}
    </BadgeContainer>
  );
};
